/**
 * Cart page: quantity stepper (same pattern as template-parts/product-hero's
 * initQuantityStepper — see initQuantitySteppers() below) and auto-update on
 * quantity change, replacing the removed "Aktualizuj koszyk" button.
 *
 * Auto-update reimplements WooCommerce core's own quantity_update() (assets/
 * js/frontend/cart.js) rather than relying on it: that function decides
 * whether to fire by checking which submit button was last clicked / which
 * input has focus, a heuristic that only works reliably for a real button
 * click or pressing Enter in the qty field — not for our +/- buttons, which
 * move focus to themselves rather than the input. Posting to the form's own
 * action URL and swapping in the response is otherwise the same approach:
 * .woocommerce-cart-form and .cart_totals are kept as extra classes
 * alongside this template's own BEM classes specifically so a WooCommerce-
 * shaped response can drop straight back into the same markup.
 *
 * Carton-mode lines: the cart always stores the real piece quantity in
 * session (inc/carton-packaging.php), so the server-rendered <input> value
 * is real pieces even on a carton-mode line — a line switched to "Kartony
 * (12 szt.)" starts at 12 (1 full carton), not 1. Left as-is, that reads as
 * "12 kartony" instead of "1 karton", and typing/stepping it up moves by
 * whole pieces instead of whole cartons. applyCartonDisplay() divides the
 * displayed number down to a carton count on carton-mode lines; the reverse
 * multiplication happens in autoUpdateCart() right before submit, so the
 * server never sees anything but real pieces.
 */

import './index.scss';

const CART_FORM_SELECTOR = '.woocommerce-cart-form';
const CART_TOTALS_SELECTOR = '.cart_totals';
const CSV_CART_NOTICE_STORAGE_KEY = 'nitaiCsvCartErrors';

let updateTimer = null;

initQuantitySteppers();
watchQuantityChanges();
watchCouponAccordion();
watchCsvCartImport();
showStashedCsvCartNotice();
watchWooCommerceCartUpdates();

/**
 * Move each WooCommerce <input class="qty"> into its own -/+ pill, matching
 * template-parts/product-hero's own stepper. Re-run after every auto-update
 * swap, since the response HTML is WooCommerce's own unenhanced markup.
 */
function initQuantitySteppers() {
	document.querySelectorAll('.cart-page__quantity .quantity').forEach((quantity) => {
		const input = quantity.querySelector('input.qty');

		if (!input || input.closest('.cart-page__qty-stepper')) return;

		applyCartonDisplay(quantity, input);

		const wrapper = document.createElement('div');
		wrapper.className = 'cart-page__qty-stepper';

		const minus = document.createElement('button');
		minus.type = 'button';
		minus.className = 'cart-page__qty-btn cart-page__qty-btn--minus';
		minus.setAttribute('aria-label', 'Zmniejsz ilość');
		minus.textContent = '−';

		const plus = document.createElement('button');
		plus.type = 'button';
		plus.className = 'cart-page__qty-btn cart-page__qty-btn--plus';
		plus.setAttribute('aria-label', 'Zwiększ ilość');
		plus.textContent = '+';

		input.parentNode.insertBefore(wrapper, input);
		wrapper.append(minus, input, plus);

		const step = () => Math.max(1, parseInt(input.step, 10) || 1);
		const min = () => Math.max(0, parseInt(input.min, 10) || 0);
		// input.max is only ever rendered when the product actually has a
		// ceiling (see global/quantity-input.php's `0 < $max_value` check —
		// unlimited stock renders no max attribute at all, so parseInt comes
		// back NaN and clampToMax() is a no-op).
		const max = () => {
			const parsed = parseInt(input.max, 10);
			return Number.isNaN(parsed) ? null : parsed;
		};
		const clampToMax = (value) => {
			const ceiling = max();
			return ceiling === null ? value : Math.min(ceiling, value);
		};

		const applyDelta = (delta) => {
			const current = parseInt(input.value, 10) || 0;
			const next = clampToMax(Math.max(min(), current + delta));

			input.value = String(next);
			input.dispatchEvent(new Event('input', { bubbles: true }));
			input.dispatchEvent(new Event('change', { bubbles: true }));
		};

		minus.addEventListener('click', () => applyDelta(-step()));
		plus.addEventListener('click', () => applyDelta(step()));

		// Typing a value directly (not just the +/- buttons) must respect the
		// same ceiling — a regular customer can't exceed stock at all here
		// (only an operator building an order on a client's behalf can, via
		// the separate admin flow in inc/operator-orders.php, which never
		// touches this page). Runs before watchQuantityChanges()'s own
		// delegated 'change' listener, since this one is bound directly on
		// the input and both fire on the same event in listener order.
		input.addEventListener('change', () => {
			const current = parseInt(input.value, 10) || 0;
			const clamped = clampToMax(Math.max(min(), current));

			if (clamped !== current) {
				input.value = String(clamped);
			}
		});
	});
}

/**
 * On a carton-mode line, rewrite the server-rendered real-pieces value down
 * to a carton count (12 pieces at carton size 12 → "1"), and switch the
 * input's own min/step/max to carton units so typing or the +/- stepper
 * below moves by whole cartons. Marks the input with data-carton-size so
 * autoUpdateCart() knows to convert back before submitting. No-ops for
 * piece-mode lines (data-carton-size only exists once switched to carton).
 *
 * @param {HTMLElement}         quantity WooCommerce's own .quantity wrapper.
 * @param {HTMLInputElement}    input    The qty input inside it.
 */
function applyCartonDisplay(quantity, input) {
	const toggle = quantity.querySelector('.nitai-unit-toggle');
	const cartonSize = toggle ? parseInt(toggle.dataset.cartonSize, 10) : 0;
	const isCartonMode = toggle?.querySelector('input[value="carton"]')?.checked;

	if (!toggle || !cartonSize || cartonSize < 2 || !isCartonMode) return;

	const realPieces = parseInt(input.value, 10) || 0;
	const realMax = parseInt(input.max, 10);

	input.value = String(Math.max(1, Math.round(realPieces / cartonSize)));
	input.min = '1';
	input.step = '1';
	input.dataset.cartonSize = String(cartonSize);

	if (!Number.isNaN(realMax) && realMax > 0) {
		input.max = String(Math.max(1, Math.floor(realMax / cartonSize)));
	}
}

/**
 * Re-apply the stepper enhancement after WooCommerce core's OWN cart-update
 * AJAX (assets/js/frontend/cart.js), which swaps in a fresh, unenhanced
 * `.woocommerce-cart-form` and fires 'updated_wc_div' — this fires for the
 * remove-item (×) button and the native "Aktualizuj koszyk" flow, neither of
 * which goes through this file's autoUpdateCart() below. Left unhandled, a
 * removal drops the custom -/+ pills back to bare inputs and, on a
 * carton-mode line, re-exposes the raw real-pieces session value instead of
 * the converted carton count (applyCartonDisplay() never re-runs either).
 *
 * WooCommerce core dispatches this event through jQuery's own event system
 * (`$(document.body).trigger('updated_wc_div')`), which — unlike a native
 * `Event` dispatch — never reaches a plain `addEventListener('updated_wc_div', ...)`.
 * jQuery ships with WooCommerce regardless of this theme's own no-jQuery
 * convention, so binding through the already-loaded global is what actually
 * observes both that core-triggered path and this file's own native dispatch
 * in autoUpdateCart() below (jQuery's `.on()` does pick up native dispatches).
 *
 * initQuantitySteppers() is idempotent — inputs already wrapped are skipped
 * via the `input.closest('.cart-page__qty-stepper')` guard — so this is safe
 * to also run redundantly after autoUpdateCart()'s own success handler.
 */
function watchWooCommerceCartUpdates() {
	if (!window.jQuery) return;

	window.jQuery(document.body).on('updated_wc_div', () => initQuantitySteppers());
}

/**
 * Debounce quantity input changes (typing, or the +/- buttons above) into a
 * single auto-update request — delegated on document.body since the form
 * itself gets replaced on every update.
 */
function watchQuantityChanges() {
	document.body.addEventListener('change', (event) => {
		if (!event.target.matches(`${CART_FORM_SELECTOR} input.qty`)) return;

		clearTimeout(updateTimer);
		updateTimer = setTimeout(() => autoUpdateCart(event.target.closest('form')), 500);
	});
}

/**
 * Open/close the "Dodaj kupon" accordion (woocommerce/checkout/form-coupon.php,
 * also reused here for the cart summary card). Delegated on document.body,
 * same as watchQuantityChanges() above, so it keeps working after
 * autoUpdateCart() replaces the whole .cart_totals node.
 */
function watchCouponAccordion() {
	document.body.addEventListener('click', (event) => {
		const toggle = event.target.closest('.coupon-accordion__toggle');

		if (!toggle) return;

		const panel = document.getElementById(toggle.getAttribute('aria-controls'));

		if (!panel) return;

		const expanded = toggle.getAttribute('aria-expanded') === 'true';

		toggle.setAttribute('aria-expanded', String(!expanded));
		panel.hidden = expanded;
	});
}

/**
 * "Dodaj produkty podając kody" (inc/csv-cart-import.php, Etap 16 part B,
 * Figma node `71:29130`) — open/close the modal, merge an uploaded file's
 * codes into the textarea (read client-side, no server round-trip just to
 * populate a text field), and submit whatever's in the textarea via AJAX.
 * On success the page reloads so the newly added lines render through the
 * normal server-side cart template; any codes that didn't resolve are
 * stashed in sessionStorage first (see showStashedCsvCartNotice()) since a
 * reload would otherwise lose them.
 */
function watchCsvCartImport() {
	document.body.addEventListener('click', (event) => {
		const openButton = event.target.closest('[data-csv-cart-open]');
		const closeTarget = event.target.closest('[data-csv-cart-close]');

		if (openButton) {
			const modal = openButton
				.closest('[data-csv-cart-import]')
				?.querySelector('[data-csv-cart-modal]');
			if (modal) modal.hidden = false;
			return;
		}

		if (closeTarget) {
			closeTarget.closest('[data-csv-cart-modal]').hidden = true;
		}
	});

	document.body.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') return;

		document.querySelectorAll('[data-csv-cart-modal]:not([hidden])').forEach((modal) => {
			modal.hidden = true;
		});
	});

	document.body.addEventListener('change', (event) => {
		const fileInput = event.target.closest('[data-csv-cart-file]');

		if (!fileInput || !fileInput.files?.length) return;

		const modal = fileInput.closest('[data-csv-cart-modal]');
		const textarea = modal?.querySelector('[data-csv-cart-textarea]');

		if (!textarea) return;

		const reader = new FileReader();

		reader.onload = () => {
			const fileText = String(reader.result ?? '').trim();
			const existing = textarea.value.trim();

			textarea.value = existing ? `${existing}, ${fileText}` : fileText;
			fileInput.value = '';
		};

		reader.readAsText(fileInput.files[0]);
	});

	document.body.addEventListener('click', (event) => {
		const submitButton = event.target.closest('[data-csv-cart-submit]');

		if (!submitButton) return;

		const modal = submitButton.closest('[data-csv-cart-modal]');
		const textarea = modal?.querySelector('[data-csv-cart-textarea]');
		const errorBox = modal?.querySelector('[data-csv-cart-modal-error]');
		const codes = textarea?.value.trim() ?? '';

		if (errorBox) {
			errorBox.hidden = true;
			errorBox.textContent = '';
		}

		if (!codes) {
			if (errorBox) {
				errorBox.textContent = 'Wpisz przynajmniej jeden kod.';
				errorBox.hidden = false;
			}
			return;
		}

		submitButton.disabled = true;

		const body = new URLSearchParams();
		body.set('action', 'nitai_csv_cart_import');
		body.set('nonce', submitButton.dataset.nonce);
		body.set('codes', codes);

		fetch(submitButton.dataset.ajaxUrl, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body,
		})
			.then((response) => response.json())
			.then((data) => {
				const errors = data?.data?.errors ?? [];
				const added = data?.data?.added ?? 0;

				if (!data?.success || (errors.length && !added)) {
					submitButton.disabled = false;

					if (errorBox) {
						errorBox.textContent = errors.length
							? errors.join(' ')
							: (data?.data?.message ?? 'Nie udało się dodać produktów.');
						errorBox.hidden = false;
					}
					return;
				}

				if (errors.length) {
					sessionStorage.setItem(CSV_CART_NOTICE_STORAGE_KEY, JSON.stringify(errors));
				}

				window.location.reload();
			})
			.catch(() => {
				submitButton.disabled = false;

				if (errorBox) {
					errorBox.textContent = 'Nie udało się dodać produktów.';
					errorBox.hidden = false;
				}
			});
	});
}

/**
 * Render any codes stashed by watchCsvCartImport() right before its reload
 * (sessionStorage survives the reload; the AJAX response itself doesn't)
 * into the notice placeholder above the cart
 * (\Theme\CsvCartImport\render_notice_placeholder()), then clear the stash
 * so it doesn't reappear on a later, unrelated page load.
 */
function showStashedCsvCartNotice() {
	const raw = sessionStorage.getItem(CSV_CART_NOTICE_STORAGE_KEY);

	if (!raw) return;

	sessionStorage.removeItem(CSV_CART_NOTICE_STORAGE_KEY);

	const notice = document.querySelector('[data-csv-cart-notice]');

	if (!notice) return;

	let errors = [];

	try {
		errors = JSON.parse(raw);
	} catch {
		return;
	}

	if (!Array.isArray(errors) || !errors.length) return;

	notice.textContent = errors.join(' ');
	notice.hidden = false;
}

/**
 * POST the cart form to its own action URL and swap in the response's cart
 * list + totals — WooCommerce's own AJAX cart-update contract, just
 * triggered unconditionally on quantity change instead of a button click.
 *
 * @param {HTMLFormElement|null} form The cart form.
 */
function autoUpdateCart(form) {
	if (!form) return;

	const list = document.querySelector('.cart-page__list');
	const summary = document.querySelector('.cart-page__summary-card');

	list?.setAttribute('aria-busy', 'true');
	summary?.setAttribute('aria-busy', 'true');

	// Convert any carton-mode inputs (see applyCartonDisplay()) to real
	// pieces before WooCommerce ever sees them, snapshot the form into a
	// FormData (which copies values at construction time, not by live
	// reference), then immediately put the carton-count display back —
	// swap and revert both happen in this same synchronous block, before
	// the browser gets a chance to paint, so the real-pieces number never
	// visibly flashes in the input while the request is in flight.
	const cartonInputs = Array.from(form.querySelectorAll('input.qty[data-carton-size]'));
	const cartonDisplayValues = cartonInputs.map((input) => input.value);

	cartonInputs.forEach((input) => {
		const cartonSize = parseInt(input.dataset.cartonSize, 10);
		const cartons = parseInt(input.value, 10) || 1;

		input.value = String(cartons * cartonSize);
	});

	const body = new FormData(form);

	cartonInputs.forEach((input, index) => {
		input.value = cartonDisplayValues[index];
	});

	fetch(form.action, {
		method: form.method || 'POST',
		body,
		credentials: 'same-origin',
	})
		.then((response) => response.text())
		.then((html) => {
			const doc = new DOMParser().parseFromString(html, 'text/html');
			const newForm = doc.querySelector(CART_FORM_SELECTOR);
			const newTotals = doc.querySelector(CART_TOTALS_SELECTOR);
			const currentForm = document.querySelector(CART_FORM_SELECTOR);
			const currentTotals = document.querySelector(CART_TOTALS_SELECTOR);

			// Cart emptied out (e.g. quantity typed down to 0) — reload so the
			// empty-cart state (a different template branch) renders properly.
			if (!newForm || !currentForm || !currentTotals) {
				window.location.reload();
				return;
			}

			currentForm.replaceWith(newForm);

			if (newTotals) {
				currentTotals.replaceWith(newTotals);
			}

			initQuantitySteppers();

			// Refreshes the header mini-cart count via WooCommerce's own
			// wc-cart-fragments script, which listens for this exact event.
			document.body.dispatchEvent(new Event('updated_wc_div'));
		})
		.catch(() => window.location.reload())
		.finally(() => {
			list?.removeAttribute('aria-busy');
			summary?.removeAttribute('aria-busy');
		});
}
